const DB_NAME = "gymnastics-uploads";
const DB_VERSION = 1;
const STORE = "queue";

export type StoredUploadKind = "video" | "magazine";
export type StoredUploadPhase = "queued" | "duplicate";

export type StoredUploadRecord = {
  id: string;
  userId: string;
  kind: StoredUploadKind;
  file: Blob;
  fileName: string;
  type: string;
  size: number;
  meta: unknown;
  phase: StoredUploadPhase;
  contentHash?: string;
  matchTitle?: string;
  matchVideoId?: string;
  order: number;
};

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB unavailable"));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: "id" });
        store.createIndex("userId", "userId", { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(request.error ?? new Error("IndexedDB open failed"));
  });
}

export function fileFromRecord(record: StoredUploadRecord): File {
  return new File([record.file], record.fileName, {
    type: record.type || "application/octet-stream",
  });
}

export async function putQueuedUpload(record: StoredUploadRecord): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      const req = tx.objectStore(STORE).put(record);
      req.onsuccess = () => resolve();
      req.onerror = () =>
        reject(req.error ?? new Error("IndexedDB write failed"));
    });
  } catch {
    // Quota or private mode — upload still runs this session.
  }
}

export async function patchQueuedUpload(
  id: string,
  patch: Partial<
    Pick<
      StoredUploadRecord,
      "phase" | "contentHash" | "matchTitle" | "matchVideoId" | "meta"
    >
  >,
): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      const store = tx.objectStore(STORE);
      const getReq = store.get(id);
      getReq.onsuccess = () => {
        const row = getReq.result as StoredUploadRecord | undefined;
        if (!row) {
          resolve();
          return;
        }
        const putReq = store.put({ ...row, ...patch });
        putReq.onsuccess = () => resolve();
        putReq.onerror = () =>
          reject(putReq.error ?? new Error("IndexedDB write failed"));
      };
      getReq.onerror = () =>
        reject(getReq.error ?? new Error("IndexedDB read failed"));
    });
  } catch {
    // Best-effort.
  }
}

export async function deleteQueuedUpload(id: string): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      const req = tx.objectStore(STORE).delete(id);
      req.onsuccess = () => resolve();
      req.onerror = () =>
        reject(req.error ?? new Error("IndexedDB delete failed"));
    });
  } catch {
    // Best-effort.
  }
}

export async function listQueuedUploads(
  userId: string,
  kind: StoredUploadKind,
): Promise<StoredUploadRecord[]> {
  try {
    const db = await openDb();
    const rows = await new Promise<StoredUploadRecord[]>((resolve, reject) => {
      const tx = db.transaction(STORE, "readonly");
      const index = tx.objectStore(STORE).index("userId");
      const req = index.getAll(userId);
      req.onsuccess = () =>
        resolve((req.result as StoredUploadRecord[]) ?? []);
      req.onerror = () =>
        reject(req.error ?? new Error("IndexedDB read failed"));
    });
    return rows
      .filter((row) => row.kind === kind && row.file && row.size > 0)
      .sort((a, b) => a.order - b.order);
  } catch {
    return [];
  }
}
